import React, { useState, useEffect } from 'react';
import './SponsorshipFlow.css';
import { DONATION_AMOUNTS } from '../../constants/donationAmounts';

// State constants
const STATES = {
  INITIAL: 'initial',
  AMOUNT: 'amount',
  PAYMENT: 'payment',
  ZELLE: 'zelle_instructions'
};

const SponsorshipFlow = ({ announcement, centerConfig, onClose, skipInitial = false }) => {
  const [currentState, setCurrentState] = useState(
    skipInitial ? STATES.AMOUNT : STATES.INITIAL
  );
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Extract payment configuration
  const paymentConfig = centerConfig?.community_features?.payment || {};
  // Always allow showing the flow - we'll handle missing payment methods gracefully
  const hasStripe = paymentConfig.stripe?.enabled;
  const hasZelle = paymentConfig.zelle?.enabled;
  const hasPaymentMethods = hasStripe || hasZelle || true; // Allow flow to work even without config

  // Event details for display - parse from announcement
  const parseEventDetails = () => {
    const title = announcement?.title || 'Event Sponsorship';
    
    // Don't show date for monthly support
    let date = '';
    if (!announcement?.isMonthlySupport) {
      if (announcement?.event_date) {
        date = new Date(announcement.event_date).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      } else if (announcement?.created_at) {
        date = new Date(announcement.created_at).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      }
    }
    
    // Extract speaker from announcement if available
    const speaker = announcement?.speaker || '';
    
    return {
      title,
      date,
      speaker,
      center: centerConfig?.name || 'Dar-e-Zainab (as)'
    };
  };
  
  const eventDetails = parseEventDetails();

  // Preset amounts - Use announcement's suggested amounts if available, otherwise use defaults
  const presetAmounts = announcement?.suggested_amounts || DONATION_AMOUNTS.default;

  // External payment link — redirect instead of built-in flow
  const externalPaymentUrl = centerConfig?.payment_link_url;

  // State transition handlers
  const handleSupportClick = () => {
    if (externalPaymentUrl) {
      // Center has an external payment page — open it
      window.open(externalPaymentUrl, '_blank', 'noopener');
      return;
    }
    console.log('Support button clicked, transitioning to AMOUNT state');
    setCurrentState(STATES.AMOUNT);
    setSelectedAmount(DONATION_AMOUNTS.defaultSelection); // Default selection (middle option)
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmount(value);
    
    if (value) {
      const numValue = parseInt(value);
      if (numValue >= 1 && numValue <= 10000) {
        setSelectedAmount(numValue);
      } else {
        setSelectedAmount(null);
      }
    } else {
      setSelectedAmount(null);
    }
  };

  // Helper function to center the sponsorship card in viewport
  const centerSponsorshipCard = (stateClass, delay = 100) => {
    setTimeout(() => {
      const sponsorshipElement = document.querySelector(`.sponsorship-flow${stateClass ? `.${stateClass}` : ''}`);
      if (sponsorshipElement) {
        const rect = sponsorshipElement.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetScroll = scrollTop + rect.top - (window.innerHeight / 2) + (rect.height / 2);
        
        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }, delay);
  };

  const handleContinueFromAmount = () => {
    if (selectedAmount) {
      setCurrentState(STATES.PAYMENT);
      // Scroll to center the card after state change
      centerSponsorshipCard('state-payment');
    }
  };

  const handleCardPayment = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      console.log('Creating Stripe checkout session...');
      console.log('Amount:', selectedAmount);
      console.log('Center ID:', centerConfig?.id || centerConfig?.center_id);
      
      // Detect environment
      const isNetlifyProduction = window.location.hostname.includes('netlify.app');
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      // Determine the correct URL for the function
      const functionUrl = isNetlifyProduction 
        ? '/.netlify/functions/create-donation-checkout'  // Production on Netlify
        : 'http://localhost:8888/.netlify/functions/create-donation-checkout';  // Local with Netlify Dev
      
      console.log('Using function URL:', functionUrl);
      console.log('Environment:', isNetlifyProduction ? 'Production' : 'Development');
      
      let response;
      try {
        response = await fetch(functionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: selectedAmount,
            announcementId: announcement?.id,
            announcementTitle: announcement?.title,
            centerCode: centerConfig?.code || 'DEZ',
            centerName: centerConfig?.name || 'Dar-e-Zainab (as)',
            donorEmail: '' // Can be collected in Stripe checkout
          })
        });
      } catch (fetchError) {
        // Network error - likely Netlify Dev not running
        if (isDevelopment && fetchError.message.includes('Failed to fetch')) {
          throw new Error(
            'Netlify Dev server not running.\n\n' +
            'To test payments locally:\n' +
            '1. Stop the current server (Ctrl+C)\n' +
            '2. Run: netlify dev\n' +
            '3. Open http://localhost:8888 in your browser\n\n' +
            'Or deploy to Netlify for production testing.'
          );
        }
        throw fetchError;
      }

      console.log('Response status:', response.status);
      
      // Check if response is ok before parsing
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response:', errorData);
        
        // Check for 404 in development
        if (response.status === 404 && isDevelopment) {
          throw new Error(
            'Netlify function not found.\n\n' +
            'Make sure you are running "netlify dev" instead of "npm start"'
          );
        }
        
        // Check for specific error about missing Stripe configuration
        if (errorData.error && errorData.error.includes('not configured')) {
          throw new Error('Stripe is not configured. Please add STRIPE_SECRET_KEY in Netlify environment variables.');
        }
        throw new Error(errorData.error || `Payment failed (${response.status})`);
      }
      
      const data = await response.json();
      console.log('Checkout session response:', data);
      
      if (data.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleZellePayment = () => {
    setCurrentState(STATES.ZELLE);
  };

  const handleBack = () => {
    switch (currentState) {
      case STATES.AMOUNT:
        setCurrentState(STATES.INITIAL);
        break;
      case STATES.PAYMENT:
        setCurrentState(STATES.AMOUNT);
        centerSponsorshipCard('state-amount');
        break;
      case STATES.ZELLE:
        setCurrentState(STATES.PAYMENT);
        centerSponsorshipCard('state-payment');
        break;
      default:
        break;
    }
  };

  const handleDone = () => {
    setCurrentState(STATES.INITIAL);
    setSelectedAmount(null);
    setCustomAmount('');
    // Center the card when returning to initial state
    centerSponsorshipCard('state-initial');
    if (onClose) onClose();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could show a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format amount with commas
  const formatAmount = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Render based on current state
  const renderContent = () => {
    switch (currentState) {
      case STATES.INITIAL:
        return (
          <div className="sponsorship-initial">
            <button 
              className="support-button-standalone"
              onClick={handleSupportClick}
              disabled={!hasPaymentMethods}
            >
              <span className="blessing-icon">💖</span>
              <span className="button-text">One Click Donate</span>
            </button>
          </div>
        );

      case STATES.AMOUNT:
        return (
          <div className="sponsorship-amount">
            <div className="amount-card">
              <div className="event-info-compact">
                <div className="center-name">{eventDetails.center}</div>
                <h3>{eventDetails.title}</h3>
                {(eventDetails.date || eventDetails.speaker) && (
                  <div className="event-meta">
                    {[eventDetails.date, eventDetails.speaker && `Speaker: ${eventDetails.speaker}`]
                      .filter(Boolean)
                      .join(' • ')}
                  </div>
                )}
              </div>
              
              <div className="amount-section">
                <h2 className="section-title">Select Contribution Amount</h2>
                
                <div className="amount-grid">
                  {presetAmounts.map(amount => (
                    <button
                      key={amount}
                      className={`amount-option ${selectedAmount === amount && !customAmount ? 'selected' : ''}`}
                      onClick={() => handleAmountSelect(amount)}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                
                <div className="custom-amount-wrapper">
                  <div className="custom-amount-field">
                    <span className="dollar-sign">$</span>
                    <input
                      type="text"
                      placeholder="Enter custom amount..."
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      className="custom-amount-input"
                      maxLength="5"
                    />
                  </div>
                  {customAmount && selectedAmount === null && (
                    <div className="amount-error">Amount must be between $1 and $10,000</div>
                  )}
                </div>
                
                <button 
                  className="continue-button"
                  onClick={handleContinueFromAmount}
                  disabled={!selectedAmount}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        );

      case STATES.PAYMENT:
        return (
          <div className="sponsorship-payment">
            <div className="payment-card">
              <div className="event-info-compact">
                <div className="center-name">{eventDetails.center}</div>
                <h3>{eventDetails.title}</h3>
                {(eventDetails.date || eventDetails.speaker) && (
                  <div className="event-meta">
                    {[eventDetails.date, eventDetails.speaker && `Speaker: ${eventDetails.speaker}`]
                      .filter(Boolean)
                      .join(' • ')}
                  </div>
                )}
              </div>
              
              <div className="amount-display">
                <div className="amount-value">${formatAmount(selectedAmount)}</div>
                <div className="amount-label">Contribution Amount</div>
              </div>
              
              <div className="payment-buttons">
                {(hasStripe || !hasZelle) && (
                  <button 
                    className="payment-button primary"
                    onClick={handleCardPayment}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : '1-Click Donate'}
                  </button>
                )}
                
                {(hasZelle || !hasStripe) && (
                  <button 
                    className="payment-button secondary"
                    onClick={handleZellePayment}
                    disabled={isProcessing}
                  >
                    Pay with Zelle
                  </button>
                )}
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <button className="back-link" onClick={handleBack}>
                ← Change amount
              </button>
            </div>
          </div>
        );

      case STATES.ZELLE:
        // Get Zelle email from config - check multiple possible locations
        const zelleEmail = paymentConfig.zelle?.email || 
                          centerConfig?.support_options?.zelle?.email ||
                          centerConfig?.support_options?.support_zelle_email ||
                          'treasurer@darezainabas.org';
        return (
          <div className="sponsorship-zelle">
            <div className="zelle-card">
              <h2 className="zelle-title">Complete Your Zelle Payment</h2>
              
              <div className="amount-display centered">
                <div className="amount-value">${formatAmount(selectedAmount)}</div>
              </div>
              
              <div className="zelle-steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <span className="step-text">Open your banking app with Zelle</span>
                </div>
                
                <div className="step">
                  <span className="step-number">2</span>
                  <span className="step-text">Select "Send Money with Zelle"</span>
                </div>
                
                <div className="step">
                  <span className="step-number">3</span>
                  <span className="step-text">Add recipient using email below</span>
                </div>
                
                <div className="step">
                  <span className="step-number">4</span>
                  <span className="step-text">Enter ${formatAmount(selectedAmount)} as the amount</span>
                </div>
                
                <div className="step">
                  <span className="step-number">5</span>
                  <span className="step-text">Add "{eventDetails.title}" as memo (please include your email address for receipt)</span>
                </div>
              </div>
              
              <div className="recipient-section">
                <div className="recipient-label">RECIPIENT EMAIL:</div>
                <div className="recipient-box">
                  <span className="recipient-email">{zelleEmail}</span>
                  <button 
                    className="copy-button"
                    onClick={() => copyToClipboard(zelleEmail)}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <button className="done-button" onClick={handleDone}>
                Done
              </button>
              
              <button className="back-link" onClick={handleBack}>
                ← Back to payment options
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`sponsorship-flow state-${currentState}`}
      style={{ '--sponsor-primary': centerConfig?.colors?.primary || '#1e4273' }}
    >
      {renderContent()}
    </div>
  );
};

export default SponsorshipFlow;